import { MiniQuestionnaireData } from "../types/miniQuestionnaire";

export const getRenforcementAdvice = (data: MiniQuestionnaireData): string => {
  switch (data.renforcement_musculaire) {
    case "au_moins_2_fois":
      return `Bravo, vous renforcez vos muscles au moins deux fois par semaine : un atout majeur pour votre santé.

Vous abaissez votre risque de mortalité (-10 à -17 %) et boostez votre santé cardio‑métabolique (Source : méta-analyse anglaise de 2022).

Vous vous protégez du diabète, des maladies cardiaques, des cancers… tout en gagnant force et autonomie.

Continuez ainsi et ajoutez un peu d'endurance : vous faites partie des 5 % d'adultes qui bougent suffisamment !`;
    
    case "moins_de_2_fois":
      return `Vous faites un peu de musculation : c'est un bon début, mais deux séances offrent de vrais bénéfices.

Une méta‑analyse anglaise de 2022 montre que 30 à 60 minutes par semaine réduisent la mortalité de 20 %.

Vous protégez votre cœur, vos os et gagnez en énergie : un impact majeur, confirmé par la science.

Essayez d'intégrer une seconde séance (même 15‑20 min à la maison) : c'est un vrai bond vers la forme.`;
    
    case "jamais":
      return `Vous n'effectuez aucun renforcement musculaire, pourtant c'est un levier de santé majeur.

Il n'est jamais trop tard : 30 minutes par semaine (3×10 minutes) réduisent déjà votre mortalité de -15 % (Source : méta-analyse anglaise de 2022).

Commencez en douceur (pompes au mur, squats légers) et augmentez progressivement.

Votre corps vous remerciera en restant plus fort, plus longtemps.`;
    
    default:
      return "";
  }
};

export const getTabacAdvice = (data: MiniQuestionnaireData): string => {
  if (data.fume_actuellement === "non") {
    let advice = `Vous ne fumez pas : une décision formidable pour vivre longtemps et en bonne santé.

Vous évitez la première cause de mortalité évitable en France (73 000 décès annuels) (source : Santé publique France).

Si vous avez arrêté, bravo : votre risque de décès se rapproche de celui d'un non‑fumeur.

Restez vigilant au tabagisme passif : gardez un environnement sans tabac pour protéger vos poumons.`;

    if (data.exposition_tabagisme_passif === "oui") {
      advice += `\n\nVous êtes régulièrement exposé(e) à la fumée de tabac sans fumer vous-même. Même une exposition passive n'est pas anodine. L'OMS rappelle qu'aucun contact avec la fumée n'est "sans danger". Si possible, évitez ou réduisez ces environnements enfumés pour préserver votre santé.`;
    } else if (data.exposition_tabagisme_passif === "non") {
      advice += `\n\nVous n'êtes pas régulièrement exposé(e) à la fumée de tabac. Excellente nouvelle : cela vous évite une part importante des risques (irritations respiratoires, maladies cardiovasculaires). Conservez ce cadre sain pour vous-même et votre entourage.`;
    }

    return advice;
  }

  if (data.fume_actuellement === "oui") {
    const annees = parseInt(data.annees_tabagisme) || 0;
    const cigarettes = parseInt(data.cigarettes_par_jour) || 0;
    const paquetAnnees = ((cigarettes / 20) * annees).toFixed(1);

    return `Vous fumez actuellement environ ${paquetAnnees} paquet(s)-année(s). Plus ce chiffre est élevé, plus le risque de maladies cardiovasculaires, de cancer ou de pathologies pulmonaires est important.

Nous vous encourageons à envisager un accompagnement pour arrêter, car même une réduction modeste du tabagisme améliore votre espérance de vie.`;
  }

  return "";
};

export const getAlcoolAdvice = (data: MiniQuestionnaireData): string => {
  switch (data.frequence_alcool) {
    case "jamais_rarement":
      return `Vous ne buvez pas ou très rarement : un bouclier contre la plupart des risques liés à l'alcool.

L'OMS le classe cancérigène certain : aucun verre n'est sans danger.

En restant sobre, vous évitez cancers, maladies cardiovasculaires, dépendance…

Vous faites partie des <20 % d'adultes non buveurs : un atout santé majeur, bravo !`;
    
    case "occasionnellement":
      return `Vous buvez 1 à 2 fois par semaine : c'est modéré, mais tout alcool comporte des risques.

Même en dessous de 1,5 L de vin/semaine (~2 verres/jour), le risque de cancers existe.

Restez sous 10 verres standard par semaine, espacez vos verres et gardez des jours sans alcool.

C'est une approche équilibrée : continuez ainsi en restant vigilant et bien informé.`;
    
    case "regulierement":
      return `Vous buvez presque chaque jour : cette habitude peut vite conduire à une tolérance ou dépendance.

Même à doses modérées, l'usage quotidien accroît fortement les risques de maladies (hypertension, cirrhose, cancers…) et de troubles cognitifs.

Les repères officiels (≤10 verres/semaine, 2/jour max) sont sans doute dépassés : essayez d'instaurer 2 jours sans alcool pour soulager votre corps.

Envisagez de réduire progressivement (par exemple 2‑3 fois/semaine) et, si besoin, parlez-en à un professionnel : vous gagnerez en énergie, en sommeil et en santé globale. (Alcool Info Service.fr)`;
    
    default:
      return "";
  }
};

export const getViandeRougeAdvice = (data: MiniQuestionnaireData): string => {
  switch (data.viande_rouge_frequence) {
    case "4_fois_plus":
      return `Vous consommez de la viande rouge très fréquemment (plus de 3-4 portions par semaine). Ce niveau de consommation dépasse les recommandations actuelles, qui préconisent de ne pas excéder 500 g de viandes rouges par semaine (soit environ 3 à 4 steaks) (source : Anses). Or, manger autant de viande rouge peut augmenter certains risques pour la santé. Le Centre International de Recherche sur le Cancer classe d'ailleurs la consommation élevée de viandes rouges comme "probablement cancérogène" pour l'homme, en particulier vis-à-vis du cancer colorectal. Des études ont également lié une forte consommation de viande rouge à un risque accru de maladies cardiovasculaires et de diabète de type 2 (Etude de l'Anses 2016).

En réduisant votre consommation de viande rouge, vous diminueriez ces risques tout en profitant des bénéfices des autres sources de protéines. L'objectif serait d'approcher au maximum les 500 g par semaine recommandés. Vous pourriez par exemple remplacer la viande rouge certains jours par de la volaille, du poisson ou des légumineuses riches en protéines végétales.

Varier ainsi vos sources de protéines (œufs, poissons, légumes secs…) vous apportera tous les nutriments nécessaires sans excès de graisses saturées. Votre cœur et votre système digestif y gagneront. Essayez d'y aller progressivement – par exemple, commencez par enlever une portion de viande rouge par semaine – et constatez les bienfaits sur votre santé sur le long terme.`;
    
    case "2_3_fois":
      return `Vous consommez de la viande rouge environ 2 à 3 fois par semaine, ce qui correspond à peu près à la limite haute des recommandations (500 g par semaine, environ 3 à 4 portions maximum) (source : Mangerbouger.fr). C'est une consommation modérée et plus raisonnable pour la santé qu'un apport quotidien. Vous limitez ainsi les risques associés à un excès de viande rouge (comme les cancers colorectaux ou les maladies cardiaques) par rapport à quelqu'un qui en mangerait tous les jours.

Pour aller encore plus loin dans la démarche santé, vous pourriez essayer de réduire légèrement cette fréquence. Par exemple, passer de 3 à 2 fois par semaine, en remplaçant le repas de viande en moins par une source de protéines plus maigre ou végétale. Les recommandations officielles encouragent en effet d'alterner sur la semaine la viande avec la volaille, le poisson, les œufs et les légumes secs. En suivant ce conseil, vous continuerez d'avoir suffisamment de protéines et de fer, tout en respectant au mieux les préconisations nutritionnelles.

Quoi qu'il en soit, votre consommation actuelle est déjà bien plus raisonnable qu'un gros mangeur de viande. En étant attentif aux portions (éviter les très grosses entrecôtes par exemple) et en diversifiant les protéines, vous agirez positivement pour votre santé.`;
    
    case "0_1_fois":
      return `Vous consommez peu ou pas de viande rouge (moins d'une fois par semaine). Cela signifie que vous respectez pleinement les recommandations actuelles, et c'est bénéfique pour votre santé. En limitant ainsi la viande rouge, vous réduisez notamment le risque de certains cancers et maladies cardiovasculaires associés à une forte consommation de viande (source : Santé publique France).

Veillez simplement à couvrir vos besoins en protéines et en fer autrement : par exemple via les légumineuses, le tofu, les œufs ou le poisson, qui sont d'excellentes alternatives. Les autorités sanitaires rappellent qu'il est important de varier les sources de protéines animales et végétales pour un bon équilibre alimentaire.

Votre choix de consommer très peu de viande rouge est en tout cas un atout : il s'inscrit dans une démarche de prévention (meilleure santé cardiaque, meilleur profil lipidique, etc.) tout en étant respectueux de l'environnement. Continuez ainsi en diversifiant vos sources de protéines, et pensez éventuellement à vérifier votre statut en fer lors de vos bilans de santé si vous êtes totalement végétarien(ne).`;
    
    default:
      return "";
  }
};

export const getRegimeMediterraneenAdvice = (data: MiniQuestionnaireData): string => {
  switch (data.regime_mediterraneen) {
    case "pas_du_tout":
      return `Votre alimentation actuelle est loin du modèle méditerranéen. Or ce mode alimentaire (aussi appelé régime crétois) est reconnu comme l'un des plus sains au monde : il est considéré comme le régime de référence par l'OMS et est associé à une baisse de la mortalité et à une moindre incidence des maladies cardio-métaboliques (sources : Institut de France ; Mangerbouger.fr).

Le régime méditerranéen se caractérise par une grande variété de légumes et fruits de saison, des céréales complètes et des légumineuses en abondance, des matières grasses de qualité (notamment l'huile d'olive), beaucoup de poisson et peu de viandes rouges ou de produits industriels sucrés. En n'adoptant pas ces principes, vous pourriez passer à côté de certains bénéfices majeurs pour la santé.

Rassurez-vous, il n'est jamais trop tard pour intégrer progressivement quelques habitudes méditerranéennes dans votre assiette. Par exemple, augmentez la part de légumes et de légumineuses dans vos repas, utilisez de l'huile d'olive comme assaisonnement, ou remplacez un dessert industriel par des fruits frais. Chaque petit changement vous rapprochera de ce modèle protecteur. Votre cœur, votre cerveau et l'ensemble de votre organisme en profiteront sur le long terme (meilleure protection cardiovasculaire, meilleur contrôle du poids, etc.).`;
    
    case "en_partie":
      return `Votre alimentation intègre déjà certains aspects du régime méditerranéen (vous utilisez peut-être de l'huile d'olive, vous consommez des légumes, etc.) : c'est un très bon début, car ce régime est réputé pour ses nombreux bienfaits. Par exemple, dans le cadre du régime méditerranéen, l'utilisation d'huile d'olive est associée à une diminution du risque de maladies cardio-vasculaires (Mangerbouger.fr ; Santé Publique France). De manière générale, plus on adopte les principes méditerranéens, plus on observe une protection contre l'hypertension, l'excès de cholestérol et d'autres troubles métaboliques.

En renforçant encore votre adhésion à ce régime (par exemple en augmentant votre consommation de légumineuses, de poisson gras et de fruits secs, et en réduisant encore les produits transformés), vous pourriez améliorer davantage votre profil santé. N'hésitez pas à explorer de nouvelles recettes d'inspiration méditerranéenne : c'est un investissement pour votre santé, sans perdre le plaisir gustatif.

Vous êtes sur la bonne voie : en continuant d'ajouter des éléments du régime méditerranéen à vos habitudes, vous protégerez de mieux en mieux votre cœur et vos artères, tout en vous régalant de plats savoureux.`;
    
    case "tout_a_fait":
      return `Votre alimentation est principalement de type méditerranéen : bravo, c'est un excellent choix pour votre santé ! Ce modèle alimentaire riche en végétaux, en bonnes graisses et en poissons est un véritable atout. Il apporte à votre corps une foule de nutriments protecteurs (antioxydants, fibres, oméga-3) qui contribuent à prévenir les maladies.

En suivant ce régime au quotidien, vous profitez d'une réduction significative des risques de maladies cardiovasculaires, de diabète et même de certains cancers. En effet, le régime méditerranéen est associé à une mortalité plus faible et à moins de maladies cardio-métaboliques dans les études scientifiques (source : Institut de France). Vous mettez donc toutes les chances de votre côté pour vieillir en bonne santé.

Continuez ainsi en conservant la variété et la convivialité propres à ce régime : partagez vos bons petits plats colorés avec vos proches, variez les poissons, les légumes et les herbes aromatiques… Votre exemple donne envie et vous assure de rester en pleine forme durablement.`;
    
    default:
      return "";
  }
};

export const getSommeilAdvice = (data: MiniQuestionnaireData): string => {
  switch (data.heures_sommeil) {
    case "moins_6h":
      return `Vous dormez moins de 6 heures : c'est insuffisant et peut entraîner fatigue, irritabilité et problèmes de santé.

À long terme, le manque de repos augmente le risque d'hypertension, de diabète ou de prise de poids.

Essayez une routine relaxante (lumière tamisée, lecture calme) et évitez les écrans avant le coucher.

Même 30 minutes supplémentaires chaque nuit amélioreront votre humeur, votre concentration et votre énergie.`;
    
    case "6_8h":
      return `Vous dormez 7 à 8 heures par nuit : parfait pour laisser à votre corps le temps de récupérer.

Ce rythme favorise la gestion du stress, l'équilibre de l'appétit et une meilleure concentration.

Conservez-le en maintenant des horaires réguliers et en compensant les rares nuits plus courtes.

Notre programme vous aidera à optimiser encore la qualité de vos nuits – un atout santé majeur !`;
    
    case "plus_8h":
      return `Vous dormez plus de 8 heures par nuit : un sommeil prolongé qui peut masquer une fatigue persistante.

Si vous vous réveillez en pleine forme, parfait ! Sinon, visez plutôt la qualité du repos (sport, alimentation, gestion du stress).

Testez un réveil un peu plus tôt pour voir si vous restez en forme tout en gagnant du temps pour vous.

Continuez à écouter votre corps : notre programme vous aidera à affiner vos routines pour un équilibre optimal.`;
    
    default:
      return "";
  }
};
